/**
 * Backend API client
 * Calls the Cloudflare Worker endpoints.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';

/**
 * Fetch a signed Cloudflare Stream manifest URL for a protected video.
 *
 * @param {string} videoId  — Cloudflare Stream video ID
 * @param {string} schoolId
 * @param {string} firebaseIdToken — current user's Firebase ID token
 * @returns {Promise<{ manifestUrl: string, expiresAt: number }>}
 */
export async function fetchVideoToken(videoId, schoolId, firebaseIdToken) {
  const response = await fetch(`${BACKEND_URL}/video-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${firebaseIdToken}`,
    },
    body: JSON.stringify({ videoId, schoolId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Video token request failed (${response.status})`);
  }
  return response.json();
}

/**
 * Notify the backend that a session was completed so it can detect overtakes.
 * Fire-and-forget safe — rejects are caught internally.
 *
 * @param {string} userId
 * @param {string} schoolId
 * @returns {Promise<{ newRank: number|null }>}
 */
export async function callOvertake(userId, schoolId) {
  try {
    const response = await fetch(`${BACKEND_URL}/overtake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, schoolId }),
    });
    if (!response.ok) return { newRank: null };
    return await response.json();
  } catch (err) {
    console.error('callOvertake failed (non-critical):', err);
    return { newRank: null };
  }
}
